import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';

const ForResearchersPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background antialiased">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            <main className="relative">
                {/* Hero Section */}
                <section className="pt-20 md:pt-32 pb-16 md:pb-24 px-4 relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute top-0 left-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                    </div>
                    
                    <div className="container mx-auto max-w-5xl">
                        <div className="text-center space-y-4 md:space-y-6">
                            <div className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium mb-2 md:mb-4">
                                <svg className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                For Researchers
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground">
                                Make Your Research<br className="hidden md:block" />
                                <span className="text-primary">Interactive and Reproducible</span>
                            </h1>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                Present your analyses and visualizations as live, web-based documents powered by Python. 
                                No HTML or servers required - just pure research, perfectly reproducible.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-2 sm:px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                            <div className="space-y-6 md:space-y-8">
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">Interactive Figures</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Go beyond static plots with interactive sliders, filters, and real-time updates. 
                                            Let readers explore your data and findings dynamically.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">Reproducible Outputs</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            One Python file captures your logic, data, and UI together. Share exactly what you ran, 
                                            making your research fully reproducible.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-4 md:p-6 relative mt-8 md:mt-0">
                                <div className="absolute -top-3 -right-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Live Example
                                </div>
                                <pre className="text-xs md:text-sm text-muted-foreground font-mono overflow-x-auto">
                                    <code>{`# A complete research notebook in one file
import preswald as pw
import pandas as pd
import numpy as np

# Load and process data
data = pd.read_csv('experiment_results.csv')
model = fit_model(data)

# Create interactive research document
app = pw.App("Model Analysis")

@app.page("Results")
def results():
    return pw.Column([
        pw.Chart(model.predictions, type="scatter"),
        pw.Slider("Parameter α", 
                 min=0, max=1, step=0.1,
                 on_change=lambda x: update_model(x)),
        pw.Table(data.describe())
    ])

# Export as standalone document
app.export("research_paper.html")`}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Use Cases */}
                <section className="py-16 md:py-24">
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Research Use Cases</h2>
                        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Paper Supplements
                                </div>
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Paper Supplements</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Create interactive supplements that bring your paper's figures to life. 
                                    Let readers explore your data and methods in depth.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Data Transparency
                                </div>
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Data Transparency</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Share your complete analysis pipeline with interactive visualizations. 
                                    Make your research fully transparent and verifiable.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Collaboration
                                </div>
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Collaboration Tools</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Create exploratory tools for collaborators. Share interactive notebooks 
                                    that make your research accessible to all team members.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Methods
                                </div>
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Reproducible Methods</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Document your methods with interactive examples. Let readers understand 
                                    your approach through hands-on exploration.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Benefits for Researchers</h2>
                        <div className="grid md:grid-cols-2 gap-6 md:gap-12">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Research Integrity
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Research Integrity</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Share exactly what you ran</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Transparent data pipelines</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Easily include in preprints and GitHub repos</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Full reproducibility guarantee</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Zero Dev Overhead
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Zero Dev Overhead</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">No Flask, Streamlit, or JavaScript</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Works with existing Python + CSV workflows</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Run locally, share via static site or archive</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Instant preview and iteration</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default ForResearchersPage; 